import { CursorPosition } from '../../../contexts/Cursor/cursorReducer';
import type { Character } from '../../../dorian-shared/types/character/Character';
import type { Audio as ECAudio } from '../../../dorian-shared/types/audio/audio';
import {
  convertBranchToEmulator,
  CTBranchDetails,
  CharacterExpression,
  AnswerTypeItem,
  ModifierTypeItem,
  HubResolveMaps,
} from './transformNodeToBranchDetails';
import type { NodeGetter } from './getNode';

export interface RenderState {
  /** Converted node ready for the emulator. */
  node: CTBranchDetails;
  /** null when the node is open but has no steps (empty node frame). */
  stepIndex: number | null;
}

/**
 * Optional dependencies for the branch-to-emulator converter.
 * All fields may be omitted — the converter applies safe defaults.
 * Wire real values here once the corresponding context providers are in scope.
 */
export interface ConverterContext {
  characters?: Character[];
  expressions?: CharacterExpression[];
  maps?: HubResolveMaps;
  audios?: ECAudio[];
  audioPath?: string | null;
  answerTypes?: AnswerTypeItem[];
  modifierTypes?: ModifierTypeItem[];
}

/**
 * A ConverterContext with all fields absent.
 * Exported so callers can spread and override specific fields without constructing
 * the full object from scratch. The converter treats each absent field as its default.
 */
export const EMPTY_CONVERTER_CTX: ConverterContext = {};

/**
 * Projects a cursor position and a node getter into a RenderState.
 *
 * Error policy (contract §6.1):
 * - cursor.stepIndex === null + node found → { node, stepIndex: null } (empty node frame).
 * - cursor.stepIndex === null + node not found → null silently (placeholder cursor, nothing to show).
 * - node not found (numeric stepIndex)  → console.warn + return null.
 * - stepIndex out of range              → console.warn + return null.
 * - converter returns undefined (branch has no id) → console.warn + return null.
 */
export function selectRenderState(
  cursor: CursorPosition,
  nodeGetter: NodeGetter,
  ctx: ConverterContext,
): RenderState | null {
  const node = nodeGetter(cursor.branchId);

  if (cursor.stepIndex === null) {
    // Empty-node frame: cursor is on the node but no step exists.
    // If the node is not yet in the getter (placeholder cursor), return null silently.
    if (!node) {
      return null;
    }

    const details = convertBranchToEmulator(
      node,
      ctx.characters ?? [],
      ctx.expressions ?? [],
      ctx.maps,
      ctx.audios ?? [],
      ctx.audioPath ?? null,
      ctx.answerTypes ?? [],
      ctx.modifierTypes ?? [],
    );

    if (!details) {
      // eslint-disable-next-line no-console
      console.warn('[selectRenderState] converter returned undefined for branchId', cursor.branchId);
      return null;
    }

    return { node: details, stepIndex: null };
  }

  if (!node) {
    // eslint-disable-next-line no-console
    console.warn('[selectRenderState] node not found for branchId', cursor.branchId);
    return null;
  }

  const stepCount = node.steps?.length ?? 0;
  if (cursor.stepIndex >= stepCount) {
    // eslint-disable-next-line no-console
    console.warn(
      '[selectRenderState] stepIndex out of range',
      cursor.stepIndex,
      '(steps:',
      stepCount,
      ', branchId:',
      cursor.branchId,
      ')',
    );
    return null;
  }

  const details = convertBranchToEmulator(
    node,
    ctx.characters ?? [],
    ctx.expressions ?? [],
    ctx.maps,
    ctx.audios ?? [],
    ctx.audioPath ?? null,
    ctx.answerTypes ?? [],
    ctx.modifierTypes ?? [],
  );

  if (!details) {
    // convertBranchToEmulator returns undefined when branch has no id (safety guard).
    // eslint-disable-next-line no-console
    console.warn('[selectRenderState] converter returned undefined for branchId', cursor.branchId);
    return null;
  }

  return { node: details, stepIndex: cursor.stepIndex };
}
